import { useState } from "react";
import { LandingHeader } from "@/components/landing/landing-header";
import { LandingFooter } from "@/components/landing/landing-footer";
import { AuthModal } from "@/components/landing/auth-modal";
import { 
  Target, 
  BarChart3, 
  Shield, 
  FileText, 
  Zap, 
  Eye,
  Users,
  Scale,
  GraduationCap
} from "lucide-react";

export default function AboutPage() {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<"login" | "register">("login");

  const openLogin = () => {
    setAuthModalTab("login");
    setAuthModalOpen(true);
  };

  const openRegister = () => {
    setAuthModalTab("register");
    setAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <LandingHeader onLoginClick={openLogin} onRegisterClick={openRegister} />

      <section className="pt-24 pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Sobre a HT CAPITAL</h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              A HT CAPITAL é uma mesa proprietária institucional criada para operar sob princípios 
              rigorosos de governança, controle de risco e avaliação objetiva de operadores.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-16">
            
            <div className="space-y-4">
              <p className="text-lg text-muted-foreground leading-relaxed">
                Nossa estrutura foi desenhada para eliminar subjetividade, ruído emocional e decisões 
                baseadas em resultado pontual, substituindo isso por um modelo técnico, auditável e 
                orientado a dados reais de execução.
              </p>
              <p className="text-lg font-medium">
                Aqui, o operador não é avaliado pelo "quanto ganhou", mas por <strong>como opera</strong>.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-primary/10">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h2 className="text-2xl font-bold">Filosofia de Operação</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Acreditamos que o mercado é um sistema de probabilidades, não um ambiente de apostas.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Por isso, toda a estrutura da HT CAPITAL foi construída para:
              </p>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <li className="flex items-center gap-2 p-3 rounded-md bg-card border border-border">
                  <div className="h-2 w-2 rounded-full bg-primary" />
                  Proteger o capital
                </li>
                <li className="flex items-center gap-2 p-3 rounded-md bg-card border border-border">
                  <div className="h-2 w-2 rounded-full bg-primary" />
                  Reduzir risco sistêmico
                </li>
                <li className="flex items-center gap-2 p-3 rounded-md bg-card border border-border">
                  <div className="h-2 w-2 rounded-full bg-primary" />
                  Formar operadores previsíveis
                </li>
                <li className="flex items-center gap-2 p-3 rounded-md bg-card border border-border">
                  <div className="h-2 w-2 rounded-full bg-primary" />
                  Sustentar operação ao longo do tempo
                </li>
              </ul>
              <div className="p-4 rounded-lg bg-card border border-border">
                <p className="text-lg font-medium">Resultado é consequência. Processo é requisito.</p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-accent/10">
                  <BarChart3 className="h-6 w-6 text-accent" />
                </div>
                <h2 className="text-2xl font-bold">Modelo Operacional</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                A HT CAPITAL opera exclusivamente em regime <strong>End of Day (EOD)</strong>.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Isso significa que:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-3 p-3 rounded-md bg-card border border-border">
                  <div className="h-2 w-2 rounded-full bg-accent mt-2" />
                  <span>Todas as métricas refletem sempre o último pregão encerrado</span>
                </li>
                <li className="flex items-start gap-3 p-3 rounded-md bg-card border border-border">
                  <div className="h-2 w-2 rounded-full bg-accent mt-2" />
                  <span>Não existem avaliações intraday</span>
                </li>
                <li className="flex items-start gap-3 p-3 rounded-md bg-card border border-border">
                  <div className="h-2 w-2 rounded-full bg-accent mt-2" />
                  <span>Não há decisões emocionais em tempo real</span>
                </li>
                <li className="flex items-start gap-3 p-3 rounded-md bg-card border border-border">
                  <div className="h-2 w-2 rounded-full bg-accent mt-2" />
                  <span>Todas as análises são feitas com dados completos</span>
                </li>
              </ul>
              <p className="text-muted-foreground">
                Esse modelo garante consistência estatística, governança técnica e histórico confiável.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-green-500/10">
                  <Eye className="h-6 w-6 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold">Avaliação Comportamental</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                O sistema de avaliação da HT CAPITAL é baseado em métricas comportamentais derivadas 
                diretamente dos dados oficiais de execução do operador.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Entre os principais fatores analisados estão:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Eficiência de captura de movimento",
                  "Exposição adversa ao risco",
                  "Frequência e padrão operacional",
                  "Comportamento após eventos de perda",
                  "Aderência ao mandato declarado",
                  "Consistência ao longo do tempo"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 rounded-md bg-card border border-border">
                    <div className="h-2 w-2 rounded-full bg-green-500" />
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground font-medium">
                Essas métricas são processadas automaticamente, sem interferência humana.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-yellow-500/10">
                  <FileText className="h-6 w-6 text-yellow-500" />
                </div>
                <h2 className="text-2xl font-bold">Mandato Operacional</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Cada operador atua sob um mandato operacional individual, que define claramente:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Limites de perda",
                  "Risco por operação",
                  "Número máximo de trades",
                  "Horários autorizados",
                  "Regras de exposição",
                  "Parâmetros de segurança"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 rounded-md bg-card border border-border">
                    <div className="h-2 w-2 rounded-full bg-yellow-500" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-lg bg-card border border-border">
                <p className="font-medium">
                  O mandato é versionado, auditável e soberano. Operar fora dele caracteriza violação.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-purple-500/10">
                  <Zap className="h-6 w-6 text-purple-500" />
                </div>
                <h2 className="text-2xl font-bold">Estrutura de Fases</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                A progressão na HT CAPITAL ocorre por fases operacionais, refletindo maturidade 
                técnica e responsabilidade sistêmica.
              </p>

              <div className="space-y-4">
                <div className="p-6 rounded-lg bg-gradient-to-r from-purple-500/10 to-transparent border-l-4 border-purple-500">
                  <h3 className="text-xl font-bold text-purple-400 mb-2">VECTOR</h3>
                  <p className="text-muted-foreground">
                    Fase de validação. O foco é comportamento, disciplina e capacidade de sustentar processo.
                  </p>
                </div>

                <div className="p-6 rounded-lg bg-gradient-to-r from-cyan-500/10 to-transparent border-l-4 border-cyan-500">
                  <h3 className="text-xl font-bold text-cyan-400 mb-2">AWAKE</h3>
                  <p className="text-muted-foreground">
                    Fase de consolidação. O operador demonstra previsibilidade e controle consistente de risco.
                  </p>
                </div>

                <div className="p-6 rounded-lg bg-gradient-to-r from-amber-500/10 to-transparent border-l-4 border-amber-500">
                  <h3 className="text-xl font-bold text-amber-400 mb-2">NOIR</h3>
                  <p className="text-muted-foreground">
                    Fase sênior. O operador atua sob parâmetros institucionais avançados, 
                    com maior responsabilidade e exigência técnica.
                  </p>
                </div>
              </div>

              <p className="text-muted-foreground font-medium">
                A progressão é automática e baseada exclusivamente em dados.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-red-500/10">
                  <Shield className="h-6 w-6 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold">Controle de Risco Sistêmico</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                A HT CAPITAL opera com múltiplas camadas de proteção de risco, incluindo:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Limites automáticos",
                  "Lockouts sistêmicos",
                  "Regressão de fase",
                  "Bloqueios operacionais",
                  "Monitoramento de padrões críticos"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 rounded-md bg-card border border-border">
                    <div className="h-2 w-2 rounded-full bg-red-500" />
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground">
                Essas medidas existem para preservar a integridade da mesa e do operador.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-blue-500/10">
                  <Eye className="h-6 w-6 text-blue-500" />
                </div>
                <h2 className="text-2xl font-bold">Transparência e Auditoria</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Toda a operação da HT CAPITAL é baseada em registros auditáveis.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Alterações de mandato, mudanças de fase, lançamentos financeiros e decisões 
                sistêmicas ficam documentadas com trilha completa de auditoria.
              </p>
              <div className="p-4 rounded-lg bg-card border border-border">
                <p className="font-medium">
                  O extrato da plataforma é a fonte oficial de apuração.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-indigo-500/10">
                  <GraduationCap className="h-6 w-6 text-indigo-500" />
                </div>
                <h2 className="text-2xl font-bold">Desenvolvimento do Operador</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                A HT CAPITAL mantém uma trilha estruturada de desenvolvimento técnico, 
                liberada conforme a fase operacional do operador.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Essa trilha aborda:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Leitura institucional de mercado",
                  "Estrutura de risco",
                  "Processo decisório",
                  "Execução profissional",
                  "Governança operacional"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 rounded-md bg-card border border-border">
                    <div className="h-2 w-2 rounded-full bg-indigo-500" />
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground">
                O objetivo é formar operadores capazes de atuar com consistência ao longo do tempo.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-orange-500/10">
                  <Users className="h-6 w-6 text-orange-500" />
                </div>
                <h2 className="text-2xl font-bold">Perfil de Operador</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                A HT CAPITAL busca operadores que:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Respeitam processo",
                  "Entendem risco como prioridade",
                  "Sustentam disciplina",
                  "Operam com responsabilidade",
                  "Aceitam governança e auditoria"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 rounded-md bg-card border border-border">
                    <div className="h-2 w-2 rounded-full bg-orange-500" />
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-muted-foreground font-medium">
                A estrutura não é desenhada para operadores que buscam atalhos, apostas ou resultados pontuais.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-teal-500/10">
                  <Scale className="h-6 w-6 text-teal-500" />
                </div>
                <h2 className="text-2xl font-bold">Compliance e Conduta</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                A HT CAPITAL opera sob regras formais de conduta e compliance, incluindo:
              </p>
              <div className="grid grid-cols-1 gap-3">
                {[
                  "Proibição de práticas abusivas",
                  "Proibição de tentativa de burlar controles",
                  "Obrigação de cooperação com auditoria interna",
                  "Aderência às regras operacionais e sistêmicas"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 rounded-md bg-card border border-border">
                    <div className="h-2 w-2 rounded-full bg-teal-500" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-lg bg-card border border-border">
                <p className="font-medium">A integridade do processo é inegociável.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <LandingFooter />

      <AuthModal 
        open={authModalOpen} 
        onOpenChange={setAuthModalOpen}
        defaultTab={authModalTab}
      />
    </div>
  );
}
